import { CARD_THEME_MAP } from '../config';
import type { CardSummary, ImageUiCopy } from '../../src/shared/contracts';
import { streamImage } from './api';
import { ImageHistory, ImageHistoryStore, type ImageHistoryEntry } from './history';
import { calculateEditDimensions, normalizePhoto } from './photo';

interface ImageViewOptions {
  card: CardSummary;
  copy: ImageUiCopy;
  imageServiceBaseUrl: string;
  onClose: () => void;
}

interface ActiveImageOperation {
  controller: AbortController;
  followingPreview: boolean;
  id: string;
  sourceId: string | null;
  step: number;
  totalSteps: number;
}

function formatCopy(template: string, values: Record<string, string | number>): string {
  return template.replace(/\{\{(\w+)\}\}/g, (match, key: string) => {
    return values[key] === undefined ? match : String(values[key]);
  });
}

function createEntryId(): string {
  return crypto.randomUUID();
}

function isAbortError(error: unknown): boolean {
  return error instanceof DOMException && error.name === 'AbortError';
}

export class ImageView {
  private readonly root: HTMLElement;
  private readonly container: HTMLElement;
  private readonly stage: HTMLElement;
  private readonly emptyState: HTMLElement;
  private readonly image: HTMLImageElement;
  private readonly historyBack: HTMLButtonElement;
  private readonly historyForward: HTMLButtonElement;
  private readonly historyDelete: HTMLButtonElement;
  private readonly historyCount: HTMLElement;
  private readonly generationStatus: HTMLButtonElement;
  private readonly addPhotoButtons: HTMLButtonElement[];
  private readonly toolbarAddPhoto: HTMLElement;
  private readonly fileInput: HTMLInputElement;
  private readonly promptForm: HTMLFormElement;
  private readonly promptLabel: HTMLLabelElement;
  private readonly promptInput: HTMLTextAreaElement;
  private readonly submitButton: HTMLButtonElement;
  private readonly errorMessage: HTMLElement;
  private readonly storageWarning: HTMLElement;
  private readonly store = new ImageHistoryStore();
  private history = new ImageHistory();
  private activeOperation: ActiveImageOperation | null = null;
  private newestReadyId: string | null = null;
  private committedImageId: string | null = null;
  private committedImageUrl: string | null = null;
  private previewImageUrl: string | null = null;
  private loading = true;
  private importingPhoto = false;
  private persistenceAvailable = true;
  private destroyed = false;

  constructor(private readonly options: ImageViewOptions) {
    const template = document.getElementById('image-container-template') as HTMLTemplateElement | null;
    if (!template) {
      throw new Error('Missing image container template.');
    }

    const fragment = template.content.cloneNode(true) as DocumentFragment;
    const root = fragment.firstElementChild as HTMLElement | null;
    if (!root) {
      throw new Error('Image template is empty.');
    }

    this.root = root;
    this.container = this.required('.image-container');
    this.stage = this.required('.image-stage');
    this.emptyState = this.required('.image-empty');
    this.image = this.required('.image-picture');
    this.historyBack = this.required('.image-history-back');
    this.historyForward = this.required('.image-history-forward');
    this.historyDelete = this.required('.image-history-delete');
    this.historyCount = this.required('.image-history-count');
    this.generationStatus = this.required('.image-generation-status');
    this.addPhotoButtons = Array.from(this.root.querySelectorAll<HTMLButtonElement>('.image-add-photo-button'));
    this.toolbarAddPhoto = this.required('.image-add-row');
    this.fileInput = this.required('.image-file-input');
    this.promptForm = this.required('.image-prompt-form');
    this.promptLabel = this.required('.image-prompt-label');
    this.promptInput = this.required('.image-prompt-input');
    this.submitButton = this.required('.image-submit-button');
    this.errorMessage = this.required('.image-error');
    this.storageWarning = this.required('.image-storage-warning');

    this.applyCopyAndTheme();
    this.bindEvents();
    this.render();
  }

  mount(): void {
    document.body.append(this.root);
    this.container.style.display = 'flex';
    void this.loadHistory();
  }

  destroy(): void {
    this.destroyed = true;
    this.activeOperation?.controller.abort();
    this.activeOperation = null;
    this.revokePreviewUrl();
    this.revokeCommittedUrl();
    this.store.close();
    this.root.remove();
  }

  private required<T extends Element>(selector: string): T {
    const element = this.root.querySelector<T>(selector);
    if (!element) {
      throw new Error(`Missing image view element: ${selector}`);
    }
    return element;
  }

  private applyCopyAndTheme(): void {
    const { card, copy } = this.options;
    const theme = CARD_THEME_MAP[card.color];
    const header = this.required<HTMLElement>('.image-header');
    header.className = `image-header p-4 flex justify-between items-center shadow-md ${theme.header}`;
    this.required<HTMLElement>('.image-title').textContent = card.name;
    this.required<HTMLElement>('.image-empty-title').textContent = copy.emptyTitle;
    this.required<HTMLElement>('.image-empty-message').textContent = card.welcomeMessage;
    this.required<HTMLButtonElement>('.image-back-button').ariaLabel = copy.backToCards;
    this.historyBack.ariaLabel = copy.previousImage;
    this.historyForward.ariaLabel = copy.nextImage;
    this.historyDelete.ariaLabel = copy.deleteImage;
    this.image.alt = copy.currentImageAlt;
    this.storageWarning.textContent = copy.storageWarning;
    for (const button of this.addPhotoButtons) {
      const label = button.querySelector('span');
      if (label) {
        label.textContent = copy.addPhoto;
      }
    }
  }

  private bindEvents(): void {
    this.required<HTMLButtonElement>('.image-back-button').addEventListener('click', () => this.options.onClose());
    this.historyBack.addEventListener('click', () => this.navigateHistory('back'));
    this.historyForward.addEventListener('click', () => this.navigateHistory('forward'));
    this.historyDelete.addEventListener('click', () => void this.deleteCurrentImage());
    this.generationStatus.addEventListener('click', () => this.jumpToNewestImage());

    for (const button of this.addPhotoButtons) {
      button.addEventListener('click', () => this.fileInput.click());
    }
    this.fileInput.addEventListener('change', () => void this.importSelectedPhoto());

    this.promptForm.addEventListener('submit', (event) => {
      event.preventDefault();
      void this.submitPrompt();
    });
  }

  private async loadHistory(): Promise<void> {
    try {
      const snapshot = await this.store.load();
      if (!this.destroyed) {
        this.history = new ImageHistory(snapshot.entries, snapshot.selectedId);
      }
    } catch {
      this.markPersistenceUnavailable();
    } finally {
      if (!this.destroyed) {
        this.loading = false;
        this.render();
      }
    }
  }

  private navigateHistory(direction: 'back' | 'forward'): void {
    const changed = direction === 'back' ? this.history.goBack() : this.history.goForward();
    if (!changed) {
      return;
    }

    this.detachFromPreview();
    if (this.history.current?.id === this.newestReadyId) {
      this.newestReadyId = null;
    }
    this.clearError();
    this.render();
    void this.persistSelection();
  }

  private jumpToNewestImage(): void {
    if (!this.newestReadyId || !this.history.selectNewest()) {
      return;
    }
    this.newestReadyId = null;
    this.clearError();
    this.render();
    void this.persistSelection();
  }

  private async deleteCurrentImage(): Promise<void> {
    if (this.activeOperation || this.loading || this.importingPhoto) {
      return;
    }

    const removed = this.history.deleteCurrent();
    if (!removed) {
      return;
    }
    if (removed.id === this.newestReadyId) {
      this.newestReadyId = null;
    }
    this.revokeCommittedUrl();
    this.clearError();
    this.render();

    if (this.persistenceAvailable) {
      try {
        await this.store.delete(removed.id, this.history.snapshot);
      } catch {
        this.markPersistenceUnavailable();
      }
    }
  }

  private async importSelectedPhoto(): Promise<void> {
    const file = this.fileInput.files?.[0];
    this.fileInput.value = '';
    if (!file || this.activeOperation || this.loading || this.importingPhoto) {
      return;
    }

    this.importingPhoto = true;
    this.clearError();
    this.render();
    try {
      const normalized = await normalizePhoto(file);
      if (this.destroyed) {
        return;
      }

      const entry: ImageHistoryEntry = {
        id: createEntryId(),
        blob: normalized.blob,
        createdAt: Date.now(),
        height: normalized.height,
        kind: 'upload',
        prompt: null,
        sourceId: null,
        width: normalized.width,
      };
      const removedIds = this.history.append(entry, true);
      this.newestReadyId = null;
      this.revokeCommittedUrl();
      this.render();
      await this.persistAppend(entry, removedIds);
    } catch {
      if (!this.destroyed) {
        this.showError(this.options.copy.photoError);
      }
    } finally {
      if (!this.destroyed) {
        this.importingPhoto = false;
        this.render();
      }
    }
  }

  private async submitPrompt(): Promise<void> {
    const prompt = this.promptInput.value.trim();
    if (!prompt || this.activeOperation || this.loading || this.importingPhoto) {
      this.promptInput.focus();
      return;
    }

    const source = this.history.current;
    const dimensions = source ? calculateEditDimensions(source.width, source.height) : { width: 1024, height: 1024 };
    const operation: ActiveImageOperation = {
      controller: new AbortController(),
      followingPreview: true,
      id: createEntryId(),
      sourceId: source?.id ?? null,
      step: 0,
      totalSteps: 4,
    };
    this.activeOperation = operation;
    this.newestReadyId = null;
    this.clearError();
    this.render();

    try {
      const result = await streamImage({
        baseUrl: this.options.imageServiceBaseUrl,
        height: dimensions.height,
        onPreview: (preview) => this.handlePreview(operation.id, preview.blob, preview.step, preview.totalSteps),
        prompt,
        signal: operation.controller.signal,
        source: source ? { blob: source.blob, height: source.height, width: source.width } : undefined,
        width: dimensions.width,
      });

      if (this.destroyed || this.activeOperation?.id !== operation.id) {
        return;
      }

      const sourceStillSelected = operation.sourceId === null
        ? this.history.current === null
        : this.history.current?.id === operation.sourceId;
      const shouldSelect = operation.followingPreview && sourceStillSelected;
      const entry: ImageHistoryEntry = {
        id: createEntryId(),
        blob: result.blob,
        createdAt: Date.now(),
        height: result.metadata.height,
        kind: source ? 'edited' : 'generated',
        prompt,
        sourceId: operation.sourceId,
        width: result.metadata.width,
      };
      const removedIds = this.history.append(entry, shouldSelect);
      this.newestReadyId = shouldSelect ? null : entry.id;
      this.promptInput.value = '';
      this.revokePreviewUrl();
      this.revokeCommittedUrl();
      this.activeOperation = null;
      this.render();
      await this.persistAppend(entry, removedIds);
    } catch (error) {
      if (!this.destroyed && !isAbortError(error)) {
        this.showError(this.options.copy.generationError);
      }
    } finally {
      if (!this.destroyed && this.activeOperation?.id === operation.id) {
        this.activeOperation = null;
        this.revokePreviewUrl();
        this.render();
      }
    }
  }

  private handlePreview(operationId: string, blob: Blob, step: number, totalSteps: number): void {
    const operation = this.activeOperation;
    if (this.destroyed || !operation || operation.id !== operationId) {
      return;
    }

    operation.step = step;
    operation.totalSteps = totalSteps;
    if (operation.followingPreview) {
      this.revokePreviewUrl();
      this.previewImageUrl = URL.createObjectURL(blob);
    }
    this.render();
  }

  private detachFromPreview(): void {
    if (this.activeOperation) {
      this.activeOperation.followingPreview = false;
    }
    this.revokePreviewUrl();
  }

  private render(): void {
    const current = this.history.current;
    const showingPreview = Boolean(this.activeOperation?.followingPreview && this.previewImageUrl);
    const hasPicture = Boolean(current || showingPreview);
    const busy = this.loading || this.importingPhoto || Boolean(this.activeOperation);

    this.emptyState.hidden = hasPicture;
    this.image.hidden = !hasPicture;
    if (showingPreview && this.previewImageUrl) {
      this.image.src = this.previewImageUrl;
    } else if (current) {
      this.showCommittedImage(current);
    } else {
      this.image.removeAttribute('src');
      this.revokeCommittedUrl();
    }

    this.stage.classList.toggle('generating', Boolean(this.activeOperation?.followingPreview));
    this.historyBack.hidden = !current;
    this.historyForward.hidden = !current;
    this.historyDelete.hidden = !current;
    this.historyCount.hidden = !current;
    this.historyBack.disabled = !this.history.canGoBack;
    this.historyForward.disabled = !this.history.canGoForward;
    this.historyDelete.disabled = busy;
    if (current) {
      this.historyCount.textContent = formatCopy(this.options.copy.imageCounterTemplate, {
        current: this.history.cursor + 1,
        total: this.history.entries.length,
      });
    }

    this.toolbarAddPhoto.hidden = !current;
    for (const button of this.addPhotoButtons) {
      button.disabled = busy;
    }
    this.promptLabel.textContent = current ? this.options.copy.promptLabelEdit : this.options.copy.promptLabelCreate;
    this.promptInput.placeholder = current
      ? this.options.copy.promptPlaceholderEdit
      : this.options.copy.promptPlaceholderCreate;
    this.promptInput.disabled = busy;
    this.submitButton.disabled = busy;
    this.submitButton.textContent = current ? this.options.copy.editButton : this.options.copy.createButton;

    this.renderGenerationStatus();
  }

  private renderGenerationStatus(): void {
    if (this.activeOperation) {
      this.generationStatus.hidden = false;
      this.generationStatus.disabled = true;
      this.generationStatus.classList.remove('ready');
      this.generationStatus.textContent = formatCopy(this.options.copy.creatingTemplate, {
        step: Math.min(this.activeOperation.step, this.activeOperation.totalSteps) + 1,
        total: this.activeOperation.totalSteps + 1,
      });
      return;
    }

    if (this.newestReadyId && this.history.current?.id !== this.newestReadyId) {
      this.generationStatus.hidden = false;
      this.generationStatus.disabled = false;
      this.generationStatus.classList.add('ready');
      this.generationStatus.textContent = this.options.copy.newImageReady;
      return;
    }

    this.generationStatus.hidden = true;
    this.generationStatus.disabled = true;
    this.generationStatus.classList.remove('ready');
  }

  private showCommittedImage(entry: ImageHistoryEntry): void {
    if (this.committedImageId === entry.id && this.committedImageUrl) {
      this.image.src = this.committedImageUrl;
      return;
    }
    this.revokeCommittedUrl();
    this.committedImageId = entry.id;
    this.committedImageUrl = URL.createObjectURL(entry.blob);
    this.image.src = this.committedImageUrl;
  }

  private revokePreviewUrl(): void {
    if (this.previewImageUrl) {
      URL.revokeObjectURL(this.previewImageUrl);
      this.previewImageUrl = null;
    }
  }

  private revokeCommittedUrl(): void {
    if (this.committedImageUrl) {
      URL.revokeObjectURL(this.committedImageUrl);
      this.committedImageUrl = null;
    }
    this.committedImageId = null;
  }

  private showError(message: string): void {
    this.errorMessage.textContent = message;
    this.errorMessage.hidden = false;
  }

  private clearError(): void {
    this.errorMessage.hidden = true;
    this.errorMessage.textContent = '';
  }

  private markPersistenceUnavailable(): void {
    this.persistenceAvailable = false;
    this.storageWarning.hidden = false;
  }

  private async persistAppend(entry: ImageHistoryEntry, removedIds: readonly string[]): Promise<void> {
    if (!this.persistenceAvailable) {
      return;
    }
    try {
      await this.store.append(entry, this.history.snapshot, removedIds);
    } catch {
      this.markPersistenceUnavailable();
    }
  }

  private async persistSelection(): Promise<void> {
    if (!this.persistenceAvailable) {
      return;
    }
    try {
      await this.store.select(this.history.snapshot);
    } catch {
      this.markPersistenceUnavailable();
    }
  }
}
